// Blog post editor — create/edit a blog_posts row: slug (auto from LV
// title), cover image, status, published_at, position, and per-locale
// copy (title, excerpt, contentHtml, meta). content is raw HTML edited in
// a plain <textarea> — no rich editor in the MVP.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Newspaper } from "lucide-react";
import { supabase, slugify } from "@vinoga/db";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { SlugInput } from "@/components/admin/SlugInput";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

type Locale = "lv" | "en" | "ru";
const LOCALES: Locale[] = ["lv", "en", "ru"];

type LocaleFields = {
  title: string;
  excerpt: string;
  contentHtml: string;
  metaTitle: string;
  metaDescription: string;
};

const emptyLocale = (): LocaleFields => ({
  title: "",
  excerpt: "",
  contentHtml: "",
  metaTitle: "",
  metaDescription: "",
});

const inputClass =
  "w-full rounded-lg border border-border bg-card px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground";
const labelClass = "mb-1 block text-xs font-semibold text-muted-foreground";

export function BlogPostEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { toast } = useToast();
  const tr = useT();

  const [tab, setTab] = useState<Locale>("lv");
  const [slug, setSlug] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [publishedAt, setPublishedAt] = useState("");
  const [position, setPosition] = useState(0);
  const [translations, setTranslations] = useState<Record<Locale, LocaleFields>>({
    lv: emptyLocale(),
    en: emptyLocale(),
    ru: emptyLocale(),
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("id", id!)
        .single();
      if (!data) {
        toast({ title: tr("common.notFound", "Not found"), variant: "destructive" });
        setLoading(false);
        return;
      }
      const d = data as any;
      setSlug(d.slug ?? "");
      setCoverImageUrl(d.cover_image_url ?? "");
      setStatus((d.status ?? "draft") as "draft" | "published");
      setPublishedAt(d.published_at ? String(d.published_at).slice(0, 10) : "");
      setPosition(d.position ?? 0);
      const stored = (d.translations ?? {}) as Record<Locale, Partial<LocaleFields>>;
      setTranslations({
        lv: { ...emptyLocale(), ...(stored.lv ?? {}) },
        en: { ...emptyLocale(), ...(stored.en ?? {}) },
        ru: { ...emptyLocale(), ...(stored.ru ?? {}) },
      });
      setLoading(false);
    })();
  }, [id, isNew, toast, tr]);

  const slugPreview = useMemo(
    () => slug.trim() || slugify(translations.lv.title),
    [slug, translations.lv.title],
  );

  const setField = useCallback(
    (locale: Locale, field: keyof LocaleFields, value: string) => {
      setTranslations((prev) => ({
        ...prev,
        [locale]: { ...prev[locale], [field]: value },
      }));
    },
    [],
  );

  const save = useCallback(async () => {
    const finalSlug = slug.trim() || slugify(translations.lv.title);
    if (!finalSlug) {
      toast({ title: tr("blog.slugRequired", "Slug is required"), variant: "destructive" });
      return;
    }
    // Stamp published_at when the post first goes live and no date is set.
    let finalPublishedAt: string | null = publishedAt || null;
    if (status === "published" && !finalPublishedAt) {
      finalPublishedAt = new Date().toISOString();
    }
    setSaving(true);
    const payload = {
      slug: finalSlug,
      translations,
      cover_image_url: coverImageUrl.trim() || null,
      status,
      published_at: finalPublishedAt,
      position,
    };
    const res = isNew
      ? await supabase.from("blog_posts").insert(payload).select("id").single()
      : await supabase.from("blog_posts").update(payload).eq("id", id!);
    setSaving(false);
    if (res.error) {
      toast({
        title: tr("common.saveFailed", "Save failed"),
        description: res.error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: tr("common.saved", "Saved") });
    navigate("/blog");
  }, [
    slug,
    translations,
    coverImageUrl,
    status,
    publishedAt,
    position,
    isNew,
    id,
    navigate,
    toast,
    tr,
  ]);

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{tr("common.loading", "Loading…")}</div>;
  }

  const t = translations[tab];

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
          <Newspaper size={20} className="text-primary" />
          {isNew ? tr("blog.new", "New post") : tr("blog.edit", "Edit post")}
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/blog")} disabled={saving}>
            {tr("common.cancel", "Cancel")}
          </Button>
          <Button onClick={save} disabled={saving} className="gap-2">
            <Save size={16} />
            {saving ? tr("common.saving", "Saving…") : tr("common.save", "Save")}
          </Button>
        </div>
      </div>

      <div className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>{tr("blog.fieldSlug", "Slug")}</label>
            <SlugInput
              value={slug}
              onChange={setSlug}
              table="blog_posts"
              excludeId={isNew ? undefined : id}
              sourceText={translations.lv.title}
              className={inputClass + " font-mono text-xs"}
              placeholder={slugify(translations.lv.title || "raksts")}
            />
            <p className="mt-1 text-xs text-muted-foreground">/blog/{slugPreview || "…"}</p>
          </div>
          <div>
            <label className={labelClass}>{tr("blog.fieldPosition", "Position")}</label>
            <input
              type="number"
              className={inputClass}
              value={position}
              onChange={(e) => setPosition(parseInt(e.target.value) || 0)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>{tr("blog.fieldStatus", "Status")}</label>
            <select
              className={inputClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as "draft" | "published")}
            >
              <option value="draft">{tr("blog.statusDraft", "draft")}</option>
              <option value="published">{tr("blog.statusPublished", "published")}</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>{tr("blog.fieldPublishedAt", "Published date")}</label>
            <input
              type="date"
              className={inputClass}
              value={publishedAt}
              onChange={(e) => setPublishedAt(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>{tr("blog.fieldCover", "Cover image")}</label>
          <ImageUpload
            value={coverImageUrl}
            onChange={setCoverImageUrl}
            bucket="blog"
            preserveAspectRatio
            placeholder={tr("blog.uploadHint", "Drop a cover image here (or click to browse)")}
          />
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-border bg-card p-5">
        <div className="mb-4 flex gap-1">
          {LOCALES.map((l) => (
            <button
              key={l}
              onClick={() => setTab(l)}
              className={
                "rounded px-3 py-1 text-xs font-semibold uppercase " +
                (tab === l ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")
              }
            >
              {l}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <Field
            label={tr("blog.fieldTitle", "Title")}
            value={t.title}
            onChange={(v) => setField(tab, "title", v)}
          />
          <Field
            label={tr("blog.fieldExcerpt", "Excerpt")}
            value={t.excerpt}
            onChange={(v) => setField(tab, "excerpt", v)}
            textarea
          />
          <div>
            <label className={labelClass}>{tr("blog.fieldContent", "Content (HTML)")}</label>
            <textarea
              className={inputClass + " min-h-[240px] resize-y font-mono text-xs"}
              value={t.contentHtml}
              onChange={(e) => setField(tab, "contentHtml", e.target.value)}
              placeholder="<p>…</p>"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {tr("blog.contentHint", "Raw HTML — rendered as-is on the storefront.")}
            </p>
          </div>
          <Field
            label={tr("blog.fieldMetaTitle", "SEO title")}
            value={t.metaTitle}
            onChange={(v) => setField(tab, "metaTitle", v)}
          />
          <Field
            label={tr("blog.fieldMetaDesc", "SEO description")}
            value={t.metaDescription}
            onChange={(v) => setField(tab, "metaDescription", v)}
            textarea
          />
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  textarea?: boolean;
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {textarea ? (
        <textarea
          className={inputClass + " min-h-[90px] resize-y"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input className={inputClass} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
